import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Icon,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import moment from "moment";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import axios from "util/Api";
import { createDebounce } from "../../util/methods";
import "./style.css";

const { RangePicker } = DatePicker;

const toId = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === "object" && value._id) {
    return value._id.toString();
  }
  return value.toString();
};

const pickSubLocationRefs = (location) => {
  if (!location || typeof location !== "object") {
    return [];
  }
  const candidates = [
    location.subLocations,
    location.sublocations,
    location.subLocation,
    location.clients,
    location.client,
  ];
  for (let index = 0; index < candidates.length; index += 1) {
    const value = candidates[index];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
};

const selectFilterByChildren = (input, option) => {
  const source = (option && option.props && option.props.children) || "";
  const text = Array.isArray(source) ? source.join(" ") : source.toString();
  return text.toLowerCase().indexOf((input || "").toLowerCase()) >= 0;
};

class SpareAdjustmentVoucherReport extends Component {
  state = {
    generating: false,
    locations: [],
    subLocations: [],
    spareOptions: [],
    spareOptionMap: {},
    spareSearchFetching: false,
    rows: [],
    filters: {
      period: [],
      location: null,
      subLocation: null,
      spares: [],
    },
  };

  spareSearchRequestSeq = 0;

  debouncedFetchSpareOptions = createDebounce((searchValue) => {
    this.fetchSpareOptionsFromApi(searchValue);
  }, 350);

  componentDidMount() {
    this.loadMastersFromAuthUser(this.props.authUser);
    this.initializeFromQuery();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.authUser !== this.props.authUser) {
      this.loadMastersFromAuthUser(this.props.authUser);
    }
  }

  componentWillUnmount() {
    if (this.debouncedFetchSpareOptions && this.debouncedFetchSpareOptions.cancel) {
      this.debouncedFetchSpareOptions.cancel();
    }
  }

  getErrorMessage = (error, fallbackMessage) => (
    (error && error.response && error.response.data && error.response.data.message)
    || fallbackMessage
  );

  tooltipLabel = (label, tip) => (
    <span className="spares-tooltip-label">
      {label}
      <Tooltip title={tip}>
        <Icon type="info-circle" className="spares-tooltip-icon" />
      </Tooltip>
    </span>
  );

  loadMastersFromAuthUser = (authUser) => {
    const rawLocations = Array.isArray(authUser && authUser.locations) ? authUser.locations : [];
    const rawClients = Array.isArray(authUser && authUser.clients) ? authUser.clients : [];

    const clientMap = {};
    rawClients.forEach((client) => {
      const clientId = toId(client && (client._id || client.id));
      if (!clientId) {
        return;
      }
      clientMap[clientId] = {
        _id: clientId,
        name: client && client.name ? client.name : "",
      };
    });

    const locations = rawLocations.map((location) => {
      const refs = pickSubLocationRefs(location);
      const subLocations = refs
        .map((entry) => {
          const refId = toId(entry && (entry._id || entry.id || entry));
          if (!refId) {
            return null;
          }
          return clientMap[refId] || {
            _id: refId,
            name: entry && entry.name ? entry.name : "",
          };
        })
        .filter((entry, index, source) => entry && source.findIndex((value) => value._id === entry._id) === index);

      return {
        ...location,
        _id: toId(location && (location._id || location.id)),
        subLocations,
      };
    }).filter((location) => !!location._id);

    this.setState({
      locations,
      subLocations: Object.values(clientMap),
    });
  };

  initializeFromQuery = async () => {
    const query = new URLSearchParams((this.props.location && this.props.location.search) || "");
    const fromDate = query.get("fromDate");
    const toDate = query.get("toDate");
    const location = query.get("location");
    const subLocation = query.get("subLocation");
    const savVoucherNo = query.get("savVoucherNo");

    const period = [];
    if (fromDate) {
      const start = moment(fromDate, "YYYY-MM-DD", true);
      if (start.isValid()) {
        period[0] = start;
      }
    }
    if (toDate) {
      const end = moment(toDate, "YYYY-MM-DD", true);
      if (end.isValid()) {
        period[1] = end;
      }
    }

    await new Promise((resolve) => this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        period: period.length === 2 ? period : [],
        location: location || null,
        subLocation: subLocation || null,
      },
    }), resolve));

    if (savVoucherNo) {
      this.props.history.push(`/spare-stock-taking-adjustment/${encodeURIComponent(savVoucherNo)}`);
      return;
    }

    if (fromDate || toDate || location || subLocation) {
      await this.generateReport();
    }
  };

  getAvailableSubLocations = () => {
    const { filters } = this.state;
    const selectedLocation = (this.state.locations || []).find((location) => (
      location && location._id && filters.location && location._id.toString() === filters.location.toString()
    ));
    const scopedSubLocations = selectedLocation && selectedLocation.subLocations && selectedLocation.subLocations.length > 0
      ? selectedLocation.subLocations
      : (this.state.subLocations || []);
    return scopedSubLocations;
  };

  fetchSpareOptionsFromApi = async (searchValue) => {
    const requestSeq = ++this.spareSearchRequestSeq;
    this.setState({ spareSearchFetching: true });
    try {
      const response = await axios.get(`/spare-part/search-spares?searchValue=${encodeURIComponent(searchValue)}&limit=50`);
      if (requestSeq !== this.spareSearchRequestSeq) {
        return;
      }
      const options = (response && response.data) || [];
      this.setState((prevState) => {
        const spareOptionMap = { ...prevState.spareOptionMap };
        options.forEach((item) => {
          if (item && item._id) {
            spareOptionMap[item._id] = item;
          }
        });
        return {
          spareOptions: options,
          spareOptionMap,
          spareSearchFetching: false,
        };
      });
    } catch (error) {
      if (requestSeq !== this.spareSearchRequestSeq) {
        return;
      }
      this.setState({ spareSearchFetching: false, spareOptions: [] });
    }
  };

  fetchSpareOptions = (searchValue) => {
    const normalized = (searchValue || "").toString().trim();
    if (!normalized || normalized.length < 2) {
      this.spareSearchRequestSeq += 1;
      if (this.debouncedFetchSpareOptions && this.debouncedFetchSpareOptions.cancel) {
        this.debouncedFetchSpareOptions.cancel();
      }
      this.setState({ spareOptions: [], spareSearchFetching: false });
      return;
    }
    this.debouncedFetchSpareOptions(normalized);
  };

  handleLocationChange = (value) => {
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        location: value || null,
        subLocation: null,
      },
    }));
  };

  handleSubLocationChange = (value) => {
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        subLocation: value || null,
      },
    }));
  };

  handlePeriodChange = (period) => {
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        period: Array.isArray(period) ? period : [],
      },
    }));
  };

  handleSpareChange = (values = []) => {
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        spares: values,
      },
    }));
  };

  resetFilters = () => {
    this.setState({
      rows: [],
      spareOptions: [],
      filters: {
        period: [],
        location: null,
        subLocation: null,
        spares: [],
      },
    });
  };

  generateReport = async () => {
    const { filters } = this.state;
    const params = {};
    if (filters.period && filters.period.length === 2) {
      params.fromDate = moment(filters.period[0]).format("YYYY-MM-DD");
      params.toDate = moment(filters.period[1]).format("YYYY-MM-DD");
    }
    if (filters.location) {
      params.location = filters.location;
    }
    if (filters.subLocation) {
      params.subLocation = filters.subLocation;
    }
    if (filters.spares && filters.spares.length > 0) {
      params.spares = filters.spares.join(",");
    }

    this.setState({ generating: true });
    try {
      const response = await axios.get("/spare-stock-taking/adjustment-voucher-report", { params });
      const rows = response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
      this.setState({ rows });
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to generate adjustment voucher report"));
      this.setState({ rows: [] });
    } finally {
      this.setState({ generating: false });
    }
  };

  formatDate = (value, withTime = false) => {
    if (!value) {
      return "-";
    }
    return moment(value).format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
  };

  formatNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
      return "-";
    }
    return numberValue;
  };

  formatAmount = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
      return "0.00";
    }
    return numberValue.toFixed(2);
  };

  formatUser = (user) => {
    if (!user) {
      return "-";
    }
    const name = user.name || "-";
    return user.email ? `${name} (${user.email})` : name;
  };

  renderStatusTag = (status) => {
    if (status === "CLOSED") {
      return <Tag color="green">CLOSED</Tag>;
    }
    if (status === "SUBMITTED") {
      return <Tag color="blue">SUBMITTED</Tag>;
    }
    return <Tag color="orange">{status || "DRAFT"}</Tag>;
  };

  getAdjustmentSummary = () => {
    const { rows } = this.state;
    const vouchers = new Set();
    let addedAmount = 0;
    let reducedAmount = 0;

    (rows || []).forEach((item) => {
      if (item && item.savVoucherNo) {
        vouchers.add(item.savVoucherNo);
      }
      const adjustmentQty = Number(item && item.adjustmentQuantity);
      const adjustmentValue = Number(item && item.value);
      const normalizedValue = Number.isFinite(adjustmentValue) ? Math.abs(adjustmentValue) : 0;
      if (!Number.isFinite(adjustmentQty) || adjustmentQty === 0) {
        return;
      }
      if (adjustmentQty > 0) {
        addedAmount += normalizedValue;
      } else {
        reducedAmount += normalizedValue;
      }
    });

    return {
      voucherCount: vouchers.size,
      addedAmount,
      reducedAmount,
      netAmount: addedAmount - reducedAmount,
    };
  };

  openVoucherDetails = (voucherNo) => {
    if (!voucherNo) {
      return;
    }
    this.props.history.push(`/spare-stock-taking-adjustment/${encodeURIComponent(voucherNo)}`);
  };

  downloadExcel = () => {
    const { rows } = this.state;

    if (!rows || !rows.length) {
      message.error("No report data available to download.");
      return;
    }

    const exportRows = rows.map((item, index) => ({
      "Sl No": index + 1,
      "SST Voucher": item.sstVoucherNo || "",
      "SST Date": item.sstDate ? moment(item.sstDate).format("DD-MM-YYYY") : "",
      "SST Status": item.sstStatus || "",
      Location: item.locationName || "",
      "Sub Location": item.subLocationName || "",
      "SAV Voucher": item.savVoucherNo || "",
      "Adjustment Sub Location": item.savSubLocationName || "",
      "Created On": item.savCreatedAt ? moment(item.savCreatedAt).format("DD-MM-YYYY HH:mm") : "",
      "Created By": this.formatUser(item.savCreatedBy),
      Spare: item.spareName || "",
      "Spare Code": item.spareCode || "",
      "Initial Qty (Before)": item.initialQuantity !== null && item.initialQuantity !== undefined ? item.initialQuantity : "",
      "Adjustment Qty": item.adjustmentQuantity !== null && item.adjustmentQuantity !== undefined ? item.adjustmentQuantity : "",
      "Final Qty (After)": item.finalQuantity !== null && item.finalQuantity !== undefined ? item.finalQuantity : "",
      "Rate Used": item.rate !== null && item.rate !== undefined ? item.rate : "",
      Value: item.value !== null && item.value !== undefined ? item.value : "",
      Remarks: item.remarks || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SAV Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob(
      [excelBuffer],
      { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" },
    );
    FileSaver.saveAs(data, `spare-adjustment-vouchers-${moment().format("YYYYMMDD-HHmmss")}.xlsx`);
  };

  render() {
    const {
      generating,
      filters,
      locations,
      spareOptions,
      spareSearchFetching,
      rows,
    } = this.state;
    const adjustmentSummary = this.getAdjustmentSummary();

    const columns = [
      {
        title: this.tooltipLabel("SST Voucher", "Source spare stock taking voucher"),
        dataIndex: "sstVoucherNo",
        key: "sstVoucherNo",
        width: 140,
      },
      {
        title: this.tooltipLabel("SST Date", "Spare stock taking date"),
        dataIndex: "sstDate",
        key: "sstDate",
        width: 120,
        render: (value) => this.formatDate(value),
      },
      {
        title: this.tooltipLabel("SST Status", "Current status of the source SST"),
        dataIndex: "sstStatus",
        key: "sstStatus",
        width: 120,
        render: (value) => this.renderStatusTag(value),
      },
      {
        title: this.tooltipLabel("Location", "Location from SST filter"),
        dataIndex: "locationName",
        key: "locationName",
        width: 180,
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Sub Location", "Sub location from SST filter"),
        dataIndex: "subLocationName",
        key: "subLocationName",
        width: 180,
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("SAV Voucher", "Spare adjustment voucher number"),
        dataIndex: "savVoucherNo",
        key: "savVoucherNo",
        width: 150,
        render: (value, record) => {
          if (!record.hasSav) {
            return <Tag>No SAV</Tag>;
          }
          return value || "-";
        },
      },
      {
        title: this.tooltipLabel("Adjustment Sub Location", "Sub location where the SAV was actually created"),
        dataIndex: "savSubLocationName",
        key: "savSubLocationName",
        width: 190,
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Created On", "SAV creation timestamp"),
        dataIndex: "savCreatedAt",
        key: "savCreatedAt",
        width: 160,
        render: (value) => this.formatDate(value, true),
      },
      {
        title: this.tooltipLabel("Created By", "User who created the SAV"),
        dataIndex: "savCreatedBy",
        key: "savCreatedBy",
        width: 220,
        render: (value) => this.formatUser(value),
      },
      {
        title: this.tooltipLabel("Spare", "Spare part in the SAV line"),
        key: "spare",
        width: 240,
        render: (_, record) => (
          <div>
            <div className="spares-cell-strong">{record.spareName || "-"}</div>
            <div className="spares-cell-subtle">{record.spareCode || "-"}</div>
          </div>
        ),
      },
      {
        title: this.tooltipLabel("Initial Qty (Before)", "Original quantity before adjustment"),
        dataIndex: "initialQuantity",
        key: "initialQuantity",
        width: 150,
        align: "right",
        render: (value) => this.formatNumber(value),
      },
      {
        title: this.tooltipLabel("Adjustment Qty", "Adjustment quantity applied in SAV"),
        dataIndex: "adjustmentQuantity",
        key: "adjustmentQuantity",
        width: 130,
        align: "right",
        render: (value) => this.formatNumber(value),
      },
      {
        title: this.tooltipLabel("Rate Used", "Rate used in the SAV adjustment"),
        dataIndex: "rate",
        key: "rate",
        width: 120,
        align: "right",
        render: (value) => this.formatNumber(value),
      },
      {
        title: this.tooltipLabel("Final Qty (After)", "Quantity after applying the adjustment"),
        dataIndex: "finalQuantity",
        key: "finalQuantity",
        width: 150,
        align: "right",
        render: (value) => this.formatNumber(value),
      },
      {
        title: this.tooltipLabel("Action", "Open detailed SAV view"),
        key: "action",
        width: 110,
        fixed: "right",
        render: (_, record) => (
          record && record.savVoucherNo ? (
            <Button size="small" onClick={() => this.openVoucherDetails(record.savVoucherNo)}>
              View
            </Button>
          ) : "-"
        ),
      },
    ];

    return (
      <div className="spares-page">
        <Card
          className="spares-panel-card spares-filter-card"
          title={this.tooltipLabel("Spare Adjustment Vouchers", "All filters are optional. Generate without filters to view all matching records.")}
          extra={(
            <div className="spares-row-actions">
              <Button className="spares-btn" onClick={this.resetFilters}>
                Reset
              </Button>
              <Button
                className="spares-btn"
                disabled={!rows || !rows.length || generating}
                onClick={this.downloadExcel}
              >
                <Icon type="download" />
                Download Excel
              </Button>
              <Button
                type="primary"
                className="spares-btn spares-btn-primary"
                loading={generating}
                onClick={this.generateReport}
              >
                Generate
              </Button>
            </div>
          )}
        >
          <div className="spares-report-filter-layout">
            <div className="spares-report-filter-main">
              <div className="spares-form-grid">
                <div className="spares-form-field">
                  <span>{this.tooltipLabel("Date Period", "Filter by SST date range")}</span>
                  <RangePicker
                    value={filters.period}
                    style={{ width: "100%" }}
                    onChange={this.handlePeriodChange}
                  />
                </div>

                <div className="spares-form-field">
                  <span>{this.tooltipLabel("Location", "Filter by SST location")}</span>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={selectFilterByChildren}
                    value={filters.location || undefined}
                    placeholder="All locations"
                    onChange={this.handleLocationChange}
                  >
                    {(locations || []).map((item) => (
                      <Select.Option key={item._id} value={item._id}>
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="spares-form-field">
                  <span>{this.tooltipLabel("Sub Location", "Filter by SST sub location")}</span>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={selectFilterByChildren}
                    value={filters.subLocation || undefined}
                    placeholder="All sub locations"
                    onChange={this.handleSubLocationChange}
                  >
                    {(this.getAvailableSubLocations() || []).map((item) => (
                      <Select.Option key={item._id} value={item._id}>
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="spares-form-field">
                  <span>{this.tooltipLabel("Spares", "Filter by one or more spare parts")}</span>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    value={filters.spares || []}
                    placeholder="Search and select spares"
                    filterOption={false}
                    notFoundContent={spareSearchFetching ? <Icon type="loading" /> : null}
                    onSearch={this.fetchSpareOptions}
                    onChange={this.handleSpareChange}
                  >
                    {(spareOptions || []).map((item) => (
                      <Select.Option key={item._id} value={item._id}>
                        {item.code ? `${item.code} - ${item.name}` : item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <div className="spares-report-filter-summary">
              <Card className="spares-stat-card" bordered={false}>
                <div className="spares-summary-card-title">
                  {this.tooltipLabel("Adjustment Value", "Summary of vouchers and value movement from the generated report")}
                </div>
                <div className="spares-summary-grid">
                  <div><span>Vouchers</span><strong>{adjustmentSummary.voucherCount}</strong></div>
                  <div><span>Added</span><strong>{this.formatAmount(adjustmentSummary.addedAmount)}</strong></div>
                  <div><span>Reduced</span><strong>{this.formatAmount(adjustmentSummary.reducedAmount)}</strong></div>
                  <div><span>Net</span><strong>{this.formatAmount(adjustmentSummary.netAmount)}</strong></div>
                </div>
              </Card>
            </div>
          </div>
        </Card>

        <Card
          className="spares-panel-card"
          title={this.tooltipLabel("Report Result", "Each SAV appears as a separate row with the source SST details repeated.")}
        >
          <Spin spinning={generating}>
            {rows && rows.length > 0 ? (
              <Table
                rowKey="key"
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                scroll={{ x: 1800 }}
              />
            ) : generating ? null : (
              <Empty description="Generate the report to view spare adjustment vouchers." />
            )}
          </Spin>
        </Card>
      </div>
    );
  }
}

const mapStateToProps = ({ auth }) => ({
  authUser: auth.authUser,
});

export default connect(mapStateToProps, {})(SpareAdjustmentVoucherReport);
